import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { InvestmentsAPI } from '../services/investments.ts';
import {
  formatCurrency,
  formatCurrencyInput,
  applyCurrencyMask,
  parseCurrency,
  getCurrencyLocale,
} from '../utils/currency.ts';
import FormPage from '../components/ui/FormPage.tsx';

/** Página dedicada — Atualizar preço (acrescenta histórico de preços). */
const UpdateInvestmentPrice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('BRL');
  const [valorDisplay, setValorDisplay] = useState('');
  const [valor, setValor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await InvestmentsAPI.get(id);
        setName(data.description);
        setCurrency(data.currency || 'BRL');
        setValor(Number(data.currentValue) || 0);
        setValorDisplay(formatCurrencyInput(Number(data.currentValue) || 0));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const mutation = useMutation(async () => InvestmentsAPI.updatePrice(String(id), { valor }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['investment', id]);
      queryClient.invalidateQueries(['investment-price-history', id]);
      queryClient.invalidateQueries(['investments']);
      queryClient.invalidateQueries(['investments-portfolio']);
      navigate(`/investments/${id}`);
    },
    onError: () => setError('Erro ao atualizar preço.'),
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const locale = getCurrencyLocale(currency);

  return (
    <FormPage
      backTo={`/investments/${id}`}
      backLabel="Voltar ao detalhe"
      breadcrumb={`Patrimônio / Investimentos / ${name} / Atualizar preço`}
      title="Atualizar preço"
      subtitle={`${name} · acrescenta ponto no histórico de preços`}
      hint={
        <>
          Informe o novo <strong>valor de mercado</strong>. O sistema atualiza a posição e{' '}
          <strong>acrescenta</strong> um registro ao histórico de preços (não sobrescreve).
        </>
      }
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(`/investments/${id}`)}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="update-price-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar preço'}
          </button>
        </>
      }
    >
      <form
        id="update-price-form"
        noValidate
        onSubmit={e => {
          e.preventDefault();
          if (valor < 0) {
            setError('Valor não pode ser negativo.');
            return;
          }
          setError('');
          mutation.mutate();
        }}
      >
        {error && (
          <div className="field-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div className="form-section-title">Marcação a mercado</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="valor">Valor de mercado ({currency}) *</label>
            <input
              id="valor"
              className="input"
              value={valorDisplay}
              onChange={e => {
                const masked = applyCurrencyMask(e.target.value, locale);
                setValorDisplay(masked);
                setValor(parseCurrency(masked, locale));
              }}
              placeholder={formatCurrency(0, currency, locale)}
              inputMode="decimal"
            />
          </div>
        </div>
      </form>
    </FormPage>
  );
};

export default UpdateInvestmentPrice;
